import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Gift, 
  Users, 
  Copy, 
  Share2, 
  Check,
  Wallet,
  Clock,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const Referral = ({ user }) => {
  const navigate = useNavigate();
  const [referralData, setReferralData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem("stelx_token");
      const [codeRes, statsRes] = await Promise.all([
        axios.get(`${API}/referral/code`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/referral/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setReferralData(codeRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching referral data:", error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = async () => {
    if (navigator.share && referralData) {
      try {
        await navigator.share({
          title: "Join Stelx Pay",
          text: `Join Stelx Pay using my referral code ${referralData.referral_code} and we both get J$1,500! 💰`,
          url: referralData.referral_link
        });
      } catch (error) {
        copyToClipboard(referralData.referral_link);
      }
    } else {
      copyToClipboard(referralData?.referral_link || "");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="px-5 py-4 flex items-center gap-4 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-white/5 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold text-xl">Refer & Earn</h1>
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto space-y-6">
        {/* Hero Card */}
        <div className="relative rounded-2xl overflow-hidden" style={{
          background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.2) 0%, rgba(6, 182, 212, 0.15) 50%, rgba(15, 23, 42, 0.95) 100%)'
        }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/20 rounded-full blur-3xl" />
          <div className="relative z-10 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-white">Invite Friends</h2>
                <p className="text-sm text-teal-300">Earn J$1,500 each!</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Share your code with friends. When they sign up and make their first transaction, 
              you both receive J$1,500 in your wallets!
            </p>
            
            {/* Referral Code Display */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-xs text-muted-foreground mb-2">Your Referral Code</p>
              <div className="flex items-center justify-between">
                <span className="font-mono text-2xl font-bold text-white tracking-wider">
                  {referralData?.referral_code || "LOADING"}
                </span>
                <button
                  onClick={() => copyToClipboard(referralData?.referral_code || "")}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-green-400" />
                  ) : (
                    <Copy className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
              </div>
            </div>

            {/* Share Button */}
            <Button 
              onClick={shareReferral}
              className="w-full mt-4 btn-primary h-12"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Referral Link
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="glass rounded-xl p-4 text-center">
            <Users className="w-5 h-5 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats?.total_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Total Referrals</p>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <Check className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats?.successful_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <Wallet className="w-5 h-5 text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">
              J${(stats?.total_earnings || 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Earned</p>
          </div>
        </div>

        {/* How it Works */}
        <div className="glass rounded-xl p-5">
          <h3 className="font-heading font-semibold text-lg mb-4">How it Works</h3>
          <div className="space-y-4">
            {[
              { step: 1, title: "Share your code", desc: "Send your unique referral code to friends" },
              { step: 2, title: "They sign up", desc: "Your friend creates a Stelx Pay account" },
              { step: 3, title: "First transaction", desc: "They complete their first deposit or transfer" },
              { step: 4, title: "Both get paid!", desc: "J$1,500 credited to both wallets instantly" }
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">{item.step}</span>
                </div>
                <div>
                  <p className="font-medium text-white">{item.title}</p>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Referral History */}
        {stats?.referral_history && stats.referral_history.length > 0 && (
          <div className="glass rounded-xl p-5">
            <h3 className="font-heading font-semibold text-lg mb-4">Referral History</h3>
            <div className="space-y-3">
              {stats.referral_history.map((ref, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <span className="text-sm font-medium">{ref.referee_name?.charAt(0) || "U"}</span>
                    </div>
                    <div>
                      <p className="font-medium text-white">{ref.referee_name || "User"}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(ref.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-medium ${
                      ref.status === "completed" ? "text-emerald-400" : "text-amber-400"
                    }`}>
                      {ref.status === "completed" ? `+J$${ref.reward.toLocaleString()}` : "Pending"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Terms */}
        <p className="text-xs text-center text-muted-foreground px-4">
          Referral rewards are credited after the referee completes their first transaction. 
          Maximum 50 referrals per user. Terms apply.
        </p>
      </main>
    </div>
  );
};

export default Referral;
